import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from "@mui/material";
import { padTileStats } from "../utils/dashboardSummary";
import { NIKAO_BRAND } from "../constants/nikaoBrand";

export const TileStat = ({ label, value, placeholder }) => (
  <Box
    minWidth={0}
    maxWidth="100%"
    visibility={placeholder ? "hidden" : "visible"}
    display="flex"
    flexDirection="column"
    gap={0.15}
  >
    <Typography
      color="text.secondary"
      display="block"
      fontSize={{ xs: "0.75rem", md: "0.8rem" }}
      lineHeight={1.25}
      fontWeight={500}
      noWrap
      title={label || undefined}
    >
      {label || "—"}
    </Typography>
    <Typography
      fontWeight={700}
      color="text.primary"
      fontSize={{ xs: "1.05rem", md: "1.2rem" }}
      lineHeight={1.2}
      noWrap
      title={value === 0 || value ? String(value) : undefined}
    >
      {value === 0 || value ? value : "—"}
    </Typography>
  </Box>
);

const DashboardInfoTile = ({
  icon,
  title,
  headerColor = NIKAO_BRAND.ocean,
  loading = false,
  stats,
  footer,
  to,
}) => {
  const navigate = useNavigate();
  const isLink = Boolean(to);
  const headerFg = NIKAO_BRAND.white;

  const go = () => {
    if (to) navigate(to);
  };

  return (
    <Card
      elevation={1}
      role={isLink ? "link" : undefined}
      tabIndex={isLink ? 0 : undefined}
      aria-label={isLink ? `Open ${title}` : undefined}
      onClick={isLink ? go : undefined}
      onKeyDown={
        isLink
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                go();
              }
            }
          : undefined
      }
      sx={{
        height: "100%",
        width: "100%",
        maxWidth: "100%",
        minHeight: { xs: 200, md: 220 },
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        bgcolor: NIKAO_BRAND.white,
        overflow: "hidden",
        position: "relative",
        isolation: "isolate",
        border: "1px solid rgba(22, 14, 43, 0.08)",
        boxShadow: "0 1px 3px rgba(22, 14, 43, 0.06)",
        cursor: isLink ? "pointer" : "default",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        ...(isLink
          ? {
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(22, 14, 43, 0.12)",
              },
              "&:focus-visible": {
                outline: `2px solid ${NIKAO_BRAND.ocean}`,
                outlineOffset: 2,
              },
            }
          : {}),
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        gap={0.75}
        minWidth={0}
        px={1.75}
        py={1.25}
        color={headerFg}
        flexShrink={0}
        sx={{
          background: headerColor,
          "& .MuiSvgIcon-root": {
            color: `${headerFg} !important`,
          },
        }}
      >
        {icon}
        <Typography
          fontWeight={700}
          color={headerFg}
          noWrap
          title={title}
          fontSize={{ xs: "0.95rem", md: "1.05rem" }}
          lineHeight={1.25}
          minWidth={0}
        >
          {title}
        </Typography>
      </Box>

      <CardContent
        sx={{
          py: 1.5,
          px: 1.75,
          flex: 1,
          minWidth: 0,
          maxWidth: "100%",
          width: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: 1,
          overflow: "hidden",
          bgcolor: NIKAO_BRAND.white,
          "&:last-child": { pb: 1.5 },
        }}
      >
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flex={1}
            minHeight={100}
          >
            <CircularProgress size={22} sx={{ color: NIKAO_BRAND.ocean }} />
          </Box>
        ) : stats ? (
          <Box
            display="grid"
            gridTemplateColumns="minmax(0, 1fr) minmax(0, 1fr)"
            gridAutoRows="auto"
            columnGap={1.5}
            rowGap={1}
            minWidth={0}
            maxWidth="100%"
            width="100%"
            alignContent="start"
            flex="0 1 auto"
          >
            {padTileStats(stats).map((stat, index) => (
              <TileStat
                key={stat.placeholder ? `empty-${index}` : stat.label}
                label={stat.label}
                value={stat.value}
                placeholder={stat.placeholder}
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No data available
          </Typography>
        )}
        {footer ? (
          <Box
            minHeight={18}
            mt="auto"
            flexShrink={0}
            minWidth={0}
            maxWidth="100%"
            width="100%"
            overflow="hidden"
          >
            {footer}
          </Box>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default DashboardInfoTile;
